import { useState, useEffect } from 'react';
import protobufProtocolService from './ProtobufProtocolService';

export default function useProtobufProtocol() {
  const [connectionState, setConnectionState] = useState({
    isConnected: false,
    connectionLost: false
  });
  const [protobufSchema, setProtobufSchema] = useState(null);
  const [protobufCharacteristics, setProtobufCharacteristics] = useState([]);
  const [protobufSchemaRoot, setProtobufSchemaRoot] = useState(null);
  const [protobufSchemaProtocol, setProtobufSchemaProtocol] = useState(null);
  const [protobufRoot, setProtobufRoot] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeService = async () => {
      await protobufProtocolService.initialize();
      setProtobufRoot(protobufProtocolService.getProtobufRoot());
      setIsInitialized(true);
      
      // Sync with current service state immediately after initialization
      const currentConnectionState = protobufProtocolService.getConnectionState();
      setConnectionState(currentConnectionState);
      setProtobufSchema(protobufProtocolService.getProtobufSchema());
      setProtobufSchemaRoot(protobufProtocolService.getProtobufSchemaRoot());
      setProtobufSchemaProtocol(protobufProtocolService.getProtobufSchemaProtocol());
      setProtobufCharacteristics(protobufProtocolService.getProtobufCharacteristics());
    };

    initializeService();
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    const handleConnectionStateChange = () => {
      setConnectionState(protobufProtocolService.getConnectionState());
    };

    const handleSchemaUpdate = () => {
      setProtobufSchema(protobufProtocolService.getProtobufSchema());
      setProtobufSchemaRoot(protobufProtocolService.getProtobufSchemaRoot());
      setProtobufSchemaProtocol(protobufProtocolService.getProtobufSchemaProtocol());
    };

    const handleCharacteristicsUpdate = () => {
      setProtobufCharacteristics(protobufProtocolService.getProtobufCharacteristics());
    };

    const handleCharacteristicChanged = () => {
      setProtobufCharacteristics(prev => [...prev]); // Trigger re-render
    };

    const handleCharacteristicUpdated = () => {
      setProtobufCharacteristics(prev => [...prev]); // Trigger re-render
    };

    const handleCharacteristicPushed = () => {
      setProtobufCharacteristics(prev => [...prev]); // Trigger re-render
    };

    const handleAutoUpdateChanged = () => {
      setProtobufCharacteristics(prev => [...prev]); // Trigger re-render
    };

    // Subscribe to service events
    protobufProtocolService.on('connectionStateChanged', handleConnectionStateChange);
    protobufProtocolService.on('schemaUpdated', handleSchemaUpdate);
    protobufProtocolService.on('characteristicsUpdated', handleCharacteristicsUpdate);
    protobufProtocolService.on('characteristicChanged', handleCharacteristicChanged);
    protobufProtocolService.on('characteristicUpdated', handleCharacteristicUpdated);
    protobufProtocolService.on('characteristicPushed', handleCharacteristicPushed);
    protobufProtocolService.on('autoUpdateChanged', handleAutoUpdateChanged);

    // Initial state sync
    handleConnectionStateChange();
    handleSchemaUpdate();
    handleCharacteristicsUpdate();

    return () => {
      // Cleanup event listeners
      protobufProtocolService.off('connectionStateChanged', handleConnectionStateChange);
      protobufProtocolService.off('schemaUpdated', handleSchemaUpdate);
      protobufProtocolService.off('characteristicsUpdated', handleCharacteristicsUpdate);
      protobufProtocolService.off('characteristicChanged', handleCharacteristicChanged);
      protobufProtocolService.off('characteristicUpdated', handleCharacteristicUpdated);
      protobufProtocolService.off('characteristicPushed', handleCharacteristicPushed);
      protobufProtocolService.off('autoUpdateChanged', handleAutoUpdateChanged);
    };
  }, [isInitialized]);

  const connect = async () => {
    const result = await protobufProtocolService.connect();
    return result;
  };

  const disconnect = () => {
    protobufProtocolService.disconnect();
  };

  const updateCharacteristicValue = (index, key, newValue) => {
    protobufProtocolService.updateCharacteristicValue(index, key, newValue);
  };

  const pushChanges = async (index) => {
    await protobufProtocolService.pushChanges(index);
  };

  const setAutoUpdate = (index, autoUpdate) => {
    protobufProtocolService.setAutoUpdate(index, autoUpdate);
  };

  const generateSchemaJson = () => {
    return protobufProtocolService.generateSchemaJson();
  };

  return {
    // State
    connectionState,
    protobufSchema,
    protobufCharacteristics,
    protobufSchemaRoot,
    protobufSchemaProtocol,
    protobufRoot,
    isInitialized,
    
    // Methods
    connect,
    disconnect,
    updateCharacteristicValue,
    pushChanges,
    setAutoUpdate,
    generateSchemaJson,
    
    // Direct service access (for advanced use cases)
    service: protobufProtocolService
  };
} 